import React, { useEffect, useState } from 'react';
import { FileIcon, AlertCircle } from 'lucide-react';

// Fields shared by the Reimbursement forms. File uploads are UploadField.js.
//
// TextField / SelectField render the <label> followed by the <input> / <select> as siblings: the
// cross-app E2E (insurance-frontend e2e/live) finds inputs by that structure. Inputs use 16px text
// on phones so iOS does not zoom in on focus.

const INPUT = 'w-full px-3 py-2.5 border rounded-xl text-base sm:text-sm text-portal-ink bg-white placeholder:text-portal-label focus:outline-none focus:ring-2 focus:ring-portal-purple focus:border-portal-purple';

const Label = ({ htmlFor, label, required }) => (
    <label htmlFor={htmlFor} className="block text-xs font-semibold text-portal-ink mb-1.5">
        {label}{required && <span className="text-red-600"> *</span>}
    </label>
);

const ErrorLine = ({ id, error }) => (error ? (
    <p id={id} className="flex items-start gap-1.5 text-xs text-red-700 mt-1.5">
        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-px" aria-hidden="true" /> {error}
    </p>
) : null);

// `id` (from fieldDomId) links the label, and is what focusFirstError focuses.
export const TextField = ({ label, required, error, id, hint, ...props }) => (
    <div>
        <Label htmlFor={id} label={label} required={required} />
        <input
            id={id}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
            {...props}
            className={`${INPUT} ${error ? 'border-red-400' : 'border-portal-border'}`}
        />
        {hint && !error && <p id={`${id}-hint`} className="text-xs text-portal-sub mt-1.5">{hint}</p>}
        <ErrorLine id={`${id}-error`} error={error} />
    </div>
);

// A dropdown styled like TextField. options: [{ value, label }].
export const SelectField = ({ label, required, error, id, options, placeholder = 'Select…', ...props }) => (
    <div>
        <Label htmlFor={id} label={label} required={required} />
        <select
            id={id}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={error ? `${id}-error` : undefined}
            {...props}
            className={`${INPUT} ${error ? 'border-red-400' : 'border-portal-border'}`}
        >
            <option value="">{placeholder}</option>
            {options.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
            ))}
        </select>
        <ErrorLine id={`${id}-error`} error={error} />
    </div>
);

// Read-only field/file summaries shared by the forms' final review step.
export const ReviewField = ({ label, value }) => (
    <div className="flex gap-3 text-sm">
        <dt className="w-32 flex-shrink-0 text-portal-sub">{label}</dt>
        <dd className="min-w-0 font-medium text-portal-ink whitespace-pre-wrap break-words">{value || '—'}</dd>
    </div>
);

// A picked file on the review step: a preview for photos, its name otherwise.
export const ReviewFile = ({ label, file }) => {
    const [preview, setPreview] = useState(null);

    useEffect(() => {
        if (!file || !/^image\/(jpe?g|png)$/.test(file.type || '')) {
            setPreview(null);
            return undefined;
        }
        const url = URL.createObjectURL(file);
        setPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    return (
        <figure className="min-w-0 flex items-center gap-3 rounded-xl border border-portal-border bg-white p-2">
            <div className="w-12 h-12 rounded-lg bg-portal-bg overflow-hidden flex items-center justify-center flex-shrink-0">
                {preview ? (
                    <img src={preview} alt={`${label} preview`} className="w-full h-full object-cover" />
                ) : (
                    <FileIcon className="w-5 h-5 text-portal-purple" aria-hidden="true" />
                )}
            </div>
            <figcaption className="min-w-0 text-xs">
                <span className="block font-semibold text-portal-ink truncate">{label}</span>
                <span className="block text-portal-sub truncate">{file ? file.name : '—'}</span>
            </figcaption>
        </figure>
    );
};
